'use client'

import { Fragment, useCallback, useLayoutEffect, useRef, useState } from 'react'

import LoadingSpinner from 'component/block/loading/spinner'
import { Portal } from 'component/ui/portal'
import { ScrollSnapItem } from 'component/ui/scroll-area'
import { HEADER_LOCATION_CONTAINER_ID } from 'constant'
import { useLocationPickerMenuTitles } from 'hook/useLocationPickerMenuTitles'
import {
  scrollIndexStore,
  useScrollIndex
} from 'store/global/feed-scroll-index'
import { useSpotLocationPickerStore } from 'store/global/location-picker'
import { useIsMobile } from 'store/server-context/device'
import type { IInStreamSpotWithHeadshotV2 } from 'type/feed'
import LocationPicker from './locationPicker'
import ScrollSpacer from './scroll-spacer'
import Spot from './spot'
import SpotList from './spot-list'
import useSpotFeed from './use-spot-feed'

const SpotFeed = () => {
  const pickedLocation = useSpotLocationPickerStore(
    state => state.pickedLocation
  )
  const isMobile = useIsMobile()
  const scrollIndex = useScrollIndex()
  const allSpotsRef = useRef<IInStreamSpotWithHeadshotV2[]>([])

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isInitialLoading
  } = useSpotFeed(pickedLocation)

  const isLoading = isInitialLoading || isFetchingNextPage

  const spots = data ?? []
  allSpotsRef.current = spots

  const menuTitles = useLocationPickerMenuTitles(pickedLocation, spots)

  const [playing, setPlaying] = useState<{
    id: string
    index: number
    title: string
  } | null>(() => {
    const previousPlaying = spots[scrollIndex]
    if (!previousPlaying) {
      return null
    }

    return {
      id: previousPlaying?.id || '',
      index: previousPlaying ? scrollIndex : 0,
      title: previousPlaying?.place?.location?.display_name || ''
    }
  })

  const handleSpotChange = useCallback(
    (index: number) => {
      const spot = allSpotsRef.current[index]
      if (!spot) {
        return
      }

      const place = spot.place
      setPlaying({
        id: spot.id,
        index,
        title: place?.location?.display_name || ''
      })

      const geoPoint = place?.geo_point
      useSpotLocationPickerStore.setState({
        currentGeo: geoPoint
          ? {
              lat: +geoPoint.lat,
              lng: +geoPoint.lng
            }
          : null
      })

      // record scroll position
      scrollIndexStore.setIndex(index)

      if (index === allSpotsRef.current.length - 1 && hasNextPage) {
        fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage]
  )

  // restore scroll position
  useLayoutEffect(() => {
    if (isLoading) {
      return
    }

    document
      .querySelector(`[data-scroll-index="${scrollIndex}"]`)
      ?.scrollIntoView()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  return (
    <>
      <Portal
        getNode={() =>
          isMobile
            ? document.getElementById(HEADER_LOCATION_CONTAINER_ID)
            : null
        }
      >
        <LocationPicker menuTitles={menuTitles} currentTitle={playing} />
      </Portal>
      <div className='relative h-full playsee-mb:bg-background_still-1st'>
        {isInitialLoading ? (
          <div className='relative h-full'>
            <LoadingSpinner />
          </div>
        ) : (
          <SpotList>
            {spots.map((spot, index) => (
              <Fragment key={spot.id}>
                <ScrollSnapItem data-scroll-index={`${index}`}>
                  <Spot
                    spot={spot}
                    onIntersect={handleSpotChange}
                    index={index}
                  />
                </ScrollSnapItem>
                <ScrollSpacer />
              </Fragment>
            ))}
          </SpotList>
        )}
      </div>
    </>
  )
}

export default SpotFeed
